import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import Modal from 'react-modal'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'

import MapRecordingView from './MapRecordingView/MapRecordingView'
import { MappingFormData } from './MapRecordingView/types'

import style from './MapRecordingModal.module.css'

interface IMapRecordingModal {
  isOpen: boolean
  onClose: () => void
  onConfirm: (formData: MappingFormData) => void
  robotInfo?: IRobotInfo
  className?: string
  confirmError?: string | null
  allowForceBaseRecord?: boolean
}

const MapRecordingModal: React.FC<IMapRecordingModal> = props => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<MappingFormData>({
    subMode: 'new_location',
  })
  const [isValid, setIsValid] = useState(false)
  const [mapViewKey, setMapViewKey] = useState(0)
  useSuppressNotificationSandwich(props.isOpen, NOTIFICATION_SANDWICH_SUPPRESS.MAP_RECORDING)

  useEffect(() => {
    if (props.isOpen) {
      setIsValid(false)
      setSaving(false)
      setMapViewKey(k => k + 1)

      if (props.robotInfo?.facilityId && props.robotInfo.id) {
        MainApi.fetchRobotMaps(undefined, 1000, undefined, props.robotInfo.facilityId, props.robotInfo.id)
          .then(res => {
            const hasBaseMap = res.results && res.results.some(m => m.is_base_map)

            setFormData({
              subMode: hasBaseMap ? 'new_location' : 'new_facility',
            })
          })
          .catch(() => {
            setFormData({ subMode: 'new_location' })
          })
      } else {
        setFormData({ subMode: 'new_location' })
      }
    }
  }, [props.isOpen, props.robotInfo?.facilityId, props.robotInfo?.id])

  const onSaveClick = async () => {
    if (!isValid) {
      return
    }
    setSaving(true)
    try {
      await props.onConfirm(formData)
    } finally {
      setSaving(false)
    }
  }

  const onCancelClick = () => {
    if (saving) {
      return
    }
    props.onClose()
  }

  const handleFormDataChange = useCallback((data: MappingFormData) => {
    setFormData(data)
  }, [])

  const handleValidationChange = useCallback((valid: boolean) => {
    setIsValid(valid)
  }, [])

  const isRobotOffline = props.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={() => {
        if (!saving) {
          props.onClose()
        }
      }}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={!saving}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{t('robot.map_recording_title')}</div>
      </div>
      <div className={style.scrollBody}>
        {isRobotOffline && (
          <div className={clsx(style.alert, style.alertError, style.modalAlert)}>
            {t('robot.map_recording_offline_error')}
          </div>
        )}
        {props.confirmError && (
          <div className={clsx(style.alert, style.alertError, style.modalAlert)}>{props.confirmError}</div>
        )}
        {props.robotInfo && (
          <MapRecordingView
            key={mapViewKey}
            robotInfo={props.robotInfo}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onValidationChange={handleValidationChange}
            isModalOpen={props.isOpen}
            isDisabled={isRobotOffline}
            allowForceBaseRecord={props.allowForceBaseRecord}
          />
        )}
      </div>

      <div className={style.footer}>
        <Button.Outlined className={style.btn} onClick={onCancelClick} disabled={saving}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || !isValid || isRobotOffline}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('robot.map_recording_start')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default MapRecordingModal
